/**
 * Readiness rail — the right-hand pane of the MBMR editor.
 *
 * Three stacked sections:
 *   1. This operation   — the selected operation's checks (ok / missing / optional)
 *   2. Blocking submission — every blocked operation in the recipe, click to jump to it
 *   3. Recipe readiness — the process/operation roll-up with a dot per row and a legend
 *
 * It follows the left pane's pattern: a fixed-width collapsible column with a small
 * chevron tab when closed, so it never steals space on a narrow screen. It collapses
 * itself below 1280px on first render and can be re-opened by hand at any width.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { Skeleton, Tooltip } from 'antd';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { useReadiness } from './ReadinessContext';
import ReadinessDot, { ReadinessLegend } from './ReadinessDot';
import { CHECK_STATUS, FINDING_FRAMING, STATES, checkColors, lifecycleColors, recordFindings, safeFraming, safeState, safeStatus, stateColors, totalBlocking } from './readinessModel';
import ApprovalStateIcon, { ApprovalStatePill, approvalTitle } from './ApprovalStateIcon';

const SectionTitle = ({ children, right, isDark }) => (
  <div
    className="flex items-center justify-between gap-2 px-3 pt-3 pb-1.5"
    style={{
      fontSize: 10.5,
      letterSpacing: '0.11em',
      textTransform: 'uppercase',
      fontWeight: 600,
      color: isDark ? defaultColors.palette.gray[400] : defaultColors.palette.gray[500],
    }}
  >
    <span>{children}</span>
    {right}
  </div>
);

const StatePill = ({ state, isDark }) => {
  const key = safeState(state);
  const { fg, bg } = stateColors(key, isDark);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
      style={{ fontSize: 11, fontWeight: 600, color: fg, backgroundColor: bg }}
    >
      <ReadinessDot state={key} size={8} />
      {STATES[key].label}
    </span>
  );
};

export default function ReadinessRail() {
  const { isDark } = useTheme();
  const {
    model,
    loading,
    error,
    source,
    blockers,
    selectedOperation,
    onNavigateToOperation,
    railOpen,
    setRailOpen,
    blockersFocused,
    lifecycle,
  } = useReadiness() || {};

  const blockersRef = useRef(null);
  const scrollRef = useRef(null);

  // Jumping to an operation (from a blocker row or the roll-up) should show that
  // operation's checks, not wherever the rail happened to be scrolled.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [selectedOperation?.operation_id]);

  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const softBorder = isDark ? defaultColors.palette.gray[700] : '#f1f5f9';
  const textColor = isDark ? '#ffffff' : '#374151';
  const mutedColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const chipBg = isDark ? '#0F172A' : '#204448';

  // "Submit — N blockers" scrolls the blocker list into view instead of failing silently.
  useEffect(() => {
    if (blockersFocused && blockersRef.current) {
      blockersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [blockersFocused]);

  const summary = model?.summary;
  const readyPct = useMemo(() => {
    if (!summary?.operations_total) return 0;
    return Math.round((summary.ready / summary.operations_total) * 100);
  }, [summary]);

  // How to word the findings section. The list itself is unchanged in every state.
  const framing = FINDING_FRAMING[safeFraming(lifecycle?.findings_framing)];

  // Findings about the record itself — its order, its bill of materials. They belong to no
  // operation, so they are listed apart and are deliberately NOT buttons: there is no operation
  // to navigate to, and a row that looks clickable and goes nowhere teaches the author to
  // distrust the ones that do.
  const recordRows = recordFindings(model);

  /** The most recent return reason, so an author opening the record sees why it came back. */
  const returnedNote = useMemo(() => {
    const row = (lifecycle?.processes || []).find((p) => p.state === 'returned' && p.returned);
    return row ? approvalTitle(row) : '';
  }, [lifecycle]);

  if (!railOpen) {
    return (
      <div className="flex-none flex items-start" style={{ width: 28 }}>
        <Tooltip
          title={
            // Operations AND the record. A collapsed rail with no dot on a record that cannot
            // be submitted is the same lie the submit button was telling, in miniature.
            totalBlocking(summary)
              ? [
                  summary?.blocking_count
                    ? `${summary.blocking_count} blocking operation${summary.blocking_count === 1 ? '' : 's'}`
                    : '',
                  summary?.record_blocking_count
                    ? `${summary.record_blocking_count} record finding${summary.record_blocking_count === 1 ? '' : 's'}`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Readiness'
          }
          placement="left"
        >
          <button
            onClick={() => setRailOpen(true)}
            className="relative flex items-center justify-center px-2 py-2 mt-1 text-white rounded-l-lg shadow-md transition"
            style={{ backgroundColor: chipBg }}
          >
            <ChevronLeft className="w-3 h-3" />
            {totalBlocking(summary) > 0 && (
              <span
                className="absolute -top-1 -left-1 rounded-full"
                style={{
                  width: 8,
                  height: 8,
                  backgroundColor: isDark ? defaultColors.errorLight : defaultColors.error,
                }}
              />
            )}
          </button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex-none h-full overflow-y-auto rounded-lg shadow-md"
      style={{ width: 300, backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
    >
      {/* header */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2.5"
        style={{ backgroundColor: cardBgColor, borderBottom: `1px solid ${softBorder}` }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <ShieldCheck className="w-4 h-4 flex-none" style={{ color: mutedColor }} />
          <span className="font-semibold truncate" style={{ fontSize: 13.5, color: textColor }}>
            Readiness
          </span>
          {source === 'mock' && (
            <Tooltip title="/bp/mbmr/readiness/ is not live yet — these states are derived locally from the recipe structure. They will be replaced by the server's answer the moment the endpoint responds.">
              <span
                className="rounded px-1.5 py-0.5 flex-none"
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: isDark ? defaultColors.warningLight : defaultColors.warning,
                  border: `1px solid ${isDark ? defaultColors.warningLight : defaultColors.warning}`,
                }}
              >
                PREVIEW
              </span>
            </Tooltip>
          )}
        </div>
        <button
          onClick={() => setRailOpen(false)}
          title="Hide the readiness pane"
          className="flex items-center justify-center px-1.5 py-1.5 text-white rounded-lg shadow-sm transition flex-none"
          style={{ backgroundColor: chipBg }}
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <Skeleton loading={loading} active paragraph={{ rows: 8 }} className="p-3">
        {error ? (
          <div className="px-3 py-4" style={{ fontSize: 12.5, color: mutedColor }}>
            Readiness is unavailable: {error}
          </div>
        ) : (
          <>
            {/* roll-up */}
            {summary && (
              <div className="px-3 pt-3">
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <span style={{ fontSize: 12.5, color: mutedColor }}>
                    {summary.ready} of {summary.operations_total} operations ready
                  </span>
                  <span style={{ fontSize: 11, color: mutedColor }}>{readyPct}%</span>
                </div>
                <div
                  className="w-full rounded-full overflow-hidden"
                  style={{ height: 5, backgroundColor: isDark ? defaultColors.palette.gray[700] : '#e5e7eb' }}
                >
                  <div
                    style={{
                      width: `${readyPct}%`,
                      height: '100%',
                      backgroundColor: isDark ? defaultColors.successLight : defaultColors.success,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Approval state — the axis readiness does not measure. It sits above the checks
                because it decides what the checks MEAN: the same amber row is a submission
                blocker on a draft and a compliance finding on an approved record. */}
            {lifecycle?.known && (
              <div className="px-3 pt-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <ApprovalStatePill state={lifecycle.state} />
                  {lifecycle.chain?.configured && lifecycle.chain.position && (
                    <span style={{ fontSize: 11, color: mutedColor }}>{lifecycle.chain.position}</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: mutedColor, lineHeight: 1.45 }}>
                  {lifecycle.state === 'returned'
                    ? returnedNote || lifecycle.editable_reason
                    : lifecycle.submit_unavailable_reason || lifecycle.editable_reason}
                </div>
                {lifecycle.anomalies?.map((a) => (
                  <div
                    key={a.key}
                    className="mt-1.5 px-2.5 py-2 rounded"
                    style={{
                      fontSize: 11.5,
                      lineHeight: 1.45,
                      color: isDark ? defaultColors.errorLight : defaultColors.error,
                      backgroundColor: stateColors('blocked', isDark).bg,
                    }}
                  >
                    {a.detail}
                  </div>
                ))}
              </div>
            )}

            {/* 1 — this operation */}
            <SectionTitle isDark={isDark}>This operation</SectionTitle>
            <div className="px-3 pb-2">
              {selectedOperation ? (
                <>
                  <div className="font-medium mb-1" style={{ fontSize: 13, color: textColor, lineHeight: 1.35 }}>
                    {selectedOperation.operation_number ? `${selectedOperation.operation_number}. ` : ''}
                    {selectedOperation.operation_name}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <StatePill state={selectedOperation.state} isDark={isDark} />
                    {selectedOperation.profile && (
                      <Tooltip
                        title={
                          selectedOperation.profile === 'generic'
                            ? 'This operation type has no readiness profile, so a default rule set is applied.'
                            : `Readiness profile applied for this operation type: ${selectedOperation.profile}`
                        }
                      >
                        <span
                          className="rounded-full px-2 py-0.5"
                          style={{
                            fontSize: 10.5,
                            color: mutedColor,
                            border: `1px solid ${borderColor}`,
                          }}
                        >
                          profile · {selectedOperation.profile}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div>
                    {selectedOperation.checks.length === 0 && (
                      <div style={{ fontSize: 12.5, color: mutedColor }}>No checks defined for this type.</div>
                    )}
                    {selectedOperation.checks.map((c, i) => {
                      const status = safeStatus(c.status);
                      return (
                        <div
                          key={`${c.key}-${i}`}
                          className="flex items-start gap-2 py-1.5"
                          style={{
                            fontSize: 12.5,
                            lineHeight: 1.4,
                            color: status === 'optional' ? mutedColor : textColor,
                            borderBottom: i < selectedOperation.checks.length - 1 ? `1px solid ${softBorder}` : 'none',
                          }}
                        >
                          <span
                            aria-label={CHECK_STATUS[status].label}
                            title={CHECK_STATUS[status].label}
                            className="flex-none font-bold"
                            style={{ fontSize: 12, color: checkColors(status, isDark), width: 12 }}
                          >
                            {CHECK_STATUS[status].glyph}
                          </span>
                          <span className="flex-1 min-w-0">
                            {c.label}
                            {status === 'missing' && c.blocking && (
                              <span
                                className="ml-1 rounded px-1"
                                style={{
                                  fontSize: 9.5,
                                  fontWeight: 700,
                                  letterSpacing: '0.05em',
                                  color: isDark ? defaultColors.errorLight : defaultColors.error,
                                  border: `1px solid ${isDark ? defaultColors.errorLight : defaultColors.error}`,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                BLOCKS
                              </span>
                            )}
                            {status === 'missing' && (c.missing_steps || []).length ? (
                              <span className="block" data-testid="steps-without-instruction"
                                    style={{ color: mutedColor, fontSize: 11.5 }}>
                                {c.missing_steps.map((st) => [st.code, st.title].filter(Boolean).join(' ')).join(' · ')}
                              </span>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: mutedColor, lineHeight: 1.45 }}>
                  Select an operation in the structure pane to see its readiness checks.
                </div>
              )}
            </div>

            {/* 2a — the record's own findings. Above the operation list on purpose: a record
                that never says what order its operations run in is not fixed by opening any one
                of them, and burying that under forty operation rows is how it stays unfixed. */}
            {recordRows.length > 0 && (
              <div style={{ borderTop: `1px solid ${softBorder}` }}>
                <SectionTitle
                  isDark={isDark}
                  right={
                    <span style={{ fontWeight: 700, color: isDark ? defaultColors.errorLight : defaultColors.error }}>
                      {recordRows.length}
                    </span>
                  }
                >
                  The record itself
                </SectionTitle>
                <div className="px-3 pb-2">
                  {recordRows.map((f) => (
                    <div
                      key={f.code}
                      className="flex items-start gap-2 rounded-md px-2 py-2 mb-1.5"
                      style={{
                        // An advisory is not a blocker and must not wear a blocker's colour.
                        backgroundColor: f.blocking ? stateColors('blocked', isDark).bg : stateColors('partial', isDark).bg,
                        color: textColor,
                        fontSize: 12.5,
                        lineHeight: 1.35,
                      }}
                    >
                      <span aria-hidden style={{ marginTop: 1, color: f.blocking ? (isDark ? defaultColors.errorLight : defaultColors.error) : mutedColor }}>
                        {f.glyph}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium">{f.label}</span>
                        {f.why && (
                          <span className="block" style={{ color: mutedColor, fontSize: 11.5 }}>
                            {f.why}
                          </span>
                        )}
                        {f.fix && (
                          <span className="block" style={{ color: mutedColor, fontSize: 11.5, marginTop: 2 }}>
                            {f.fix}
                            {f.where ? ` — ${f.where}.` : ''}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2 — findings. The rows are identical whatever the record's state; only the
                heading and the sentence around them change. An approved MBMR whose filter
                integrity test has no bubble-point limit is a genuine compliance finding, and
                hiding it once someone has signed the record would be the wrong kind of tidy. */}
            <div ref={blockersRef} style={{ borderTop: `1px solid ${softBorder}` }}>
              <SectionTitle
                isDark={isDark}
                right={
                  <span style={{ fontWeight: 700, color: blockers?.length ? (isDark ? defaultColors.errorLight : defaultColors.error) : mutedColor }}>
                    {blockers?.length || 0}
                  </span>
                }
              >
                {framing.title}
              </SectionTitle>
              {framing.note && blockers?.length > 0 && (
                <div
                  className="mx-3 mb-2 px-2.5 py-2 rounded"
                  style={{
                    fontSize: 11.5,
                    lineHeight: 1.45,
                    color: mutedColor,
                    backgroundColor: lifecycleColors(lifecycle?.state, isDark).bg,
                  }}
                >
                  {framing.note}
                </div>
              )}
              <div
                className="px-3 pb-2"
                style={{
                  borderRadius: 8,
                  transition: 'background-color .4s',
                  backgroundColor: blockersFocused ? stateColors('blocked', isDark).bg : 'transparent',
                }}
              >
                {blockers?.length ? (
                  blockers.map((op) => (
                    <button
                      key={op.operation_id}
                      onClick={() => onNavigateToOperation?.(op.operation_id)}
                      className="w-full text-left flex items-start gap-2 rounded-md px-2 py-2 mb-1.5 transition"
                      style={{
                        backgroundColor: stateColors('blocked', isDark).bg,
                        color: textColor,
                        fontSize: 12.5,
                        lineHeight: 1.35,
                      }}
                    >
                      <ReadinessDot state="blocked" size={9} style={{ marginTop: 2 }} />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium">
                          {op.operation_number ? `${op.operation_number}. ` : ''}
                          {op.operation_name}
                        </span>
                        <span className="block" style={{ color: mutedColor, fontSize: 11.5 }}>
                          {op.blocking_reasons?.[0]?.replace(' — missing, blocks submission', '') ||
                            'Blocking check missing'}
                          {op.blocking_reasons?.length > 1 ? ` (+${op.blocking_reasons.length - 1} more)` : ''}
                        </span>
                      </span>
                    </button>
                  ))
                ) : (
                  <div style={{ fontSize: 12.5, color: mutedColor }}>{framing.empty}</div>
                )}
              </div>
            </div>

            {/* 3 — recipe readiness roll-up (process dots + operation dots) */}
            <div style={{ borderTop: `1px solid ${softBorder}` }}>
              <SectionTitle isDark={isDark}>Recipe readiness</SectionTitle>
              <div className="px-2 pb-1">
                {(model?.processes || []).map((p) => (
                  <div key={p.process_id} className="mb-1.5">
                    <div
                      className="flex items-center gap-2 px-1.5 py-1 rounded"
                      style={{ fontSize: 12.5, fontWeight: 600, color: textColor }}
                    >
                      <ReadinessDot state={p.state} size={9} />
                      <span className="truncate" title={p.process_name}>
                        {p.process_name}
                      </span>
                      {/* Two axes on one row: the dot is readiness, the shield is approval. */}
                      {lifecycle?.byProcess?.[p.process_id] && (
                        <ApprovalStateIcon row={lifecycle.byProcess[p.process_id]} size={13} />
                      )}
                    </div>
                    {p.operations.map((op) => (
                      <button
                        key={op.operation_id}
                        onClick={() => onNavigateToOperation?.(op.operation_id)}
                        title={`${op.operation_name} — ${STATES[safeState(op.state)].label}`}
                        className="w-full text-left flex items-center gap-2 rounded px-1.5 py-1 hover:opacity-80 transition"
                        style={{
                          fontSize: 12,
                          paddingLeft: 18,
                          color:
                            String(op.operation_id) === String(selectedOperation?.operation_id)
                              ? textColor
                              : mutedColor,
                          fontWeight:
                            String(op.operation_id) === String(selectedOperation?.operation_id) ? 600 : 400,
                          backgroundColor:
                            String(op.operation_id) === String(selectedOperation?.operation_id)
                              ? isDark
                                ? defaultColors.palette.gray[700]
                                : '#f1f5f9'
                              : 'transparent',
                        }}
                      >
                        <ReadinessDot state={op.state} size={8} />
                        <span className="truncate">{op.operation_name}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <div className="px-3 py-2.5" style={{ borderTop: `1px solid ${softBorder}` }}>
                <ReadinessLegend />
              </div>
            </div>
          </>
        )}
      </Skeleton>
    </div>
  );
}
